import {
  INTRANET_IMAGE_ACCEPT,
  INTRANET_IMAGE_MAX_BYTES,
  intranetImageMaxMb,
} from '@gb-ticket/shared-types';
import { ArrowLeftIcon } from 'lucide-react';
import { type FormEvent, useState } from 'react';
import { Link } from 'react-router';
import { toast } from 'sonner';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { usePageTitle } from '@/hooks/use-page-title';
import { ApiRequestError } from '@/lib/api';
import { PlatformPageHeader } from '@/platform/components/platform-page-header';
import { IntranetHeroBanner } from '@/products/intranet/components/intranet-hero';
import { intranetPanelClassName } from '@/products/intranet/features/intranet/intranet-panel';
import {
  useIntranetAppearance,
  useUpdateIntranetAppearance,
  useUploadIntranetAppearance,
} from '@/products/intranet/features/intranet/use-intranet';
import { INTRANET_EDITORIAL_PATH } from '@/products/intranet/intranet-paths';

const ALLOWED = new Set(['image/jpeg', 'image/png', 'image/webp']);

export function AppearancePage() {
  usePageTitle('Erscheinungsbild');
  const appearance = useIntranetAppearance();
  const update = useUpdateIntranetAppearance();
  const upload = useUploadIntranetAppearance();
  const current = appearance.data;
  const [title, setTitle] = useState<string | null>(null);
  const [alt, setAlt] = useState<string | null>(null);
  const [file, setFile] = useState<File | null>(null);
  const [preview, setPreview] = useState<string | null>(null);
  const shownTitle = title ?? current?.title ?? 'Intranet';
  const shownAlt = alt ?? current?.backgroundAlt ?? '';
  const imageMaxBytes = current?.imageMaxBytes ?? INTRANET_IMAGE_MAX_BYTES;
  const imageMaxMb = intranetImageMaxMb(imageMaxBytes);
  const saving = update.isPending || upload.isPending;

  function onFile(next: File | null) {
    setPreview((previous) => {
      if (previous) {
        URL.revokeObjectURL(previous);
      }
      return next ? URL.createObjectURL(next) : null;
    });
    setFile(next);
  }

  async function onPickFile(next: File | null) {
    await appearance.refetch();
    onFile(next);
  }

  async function onSubmit(event: FormEvent) {
    event.preventDefault();
    const refreshed = await appearance.refetch();
    const limit = refreshed.data?.imageMaxBytes ?? INTRANET_IMAGE_MAX_BYTES;
    const limitMb = intranetImageMaxMb(limit);
    if (file && (file.size > limit || !ALLOWED.has(file.type))) {
      toast.error(`Erlaubt sind JPEG, PNG und WebP bis ${limitMb} MB.`);
      return;
    }
    if (file && shownAlt.trim().length < 3) {
      toast.error('Bitte einen Alternativtext mit mindestens 3 Zeichen eingeben.');
      return;
    }
    try {
      await update.mutateAsync({
        title: shownTitle.trim(),
        ...(shownAlt.trim().length >= 3 ? { backgroundAlt: shownAlt.trim() } : {}),
      });
      if (file) {
        await upload.mutateAsync({ file, alt: shownAlt.trim() });
        onFile(null);
      }
      toast.success('Erscheinungsbild gespeichert.');
    } catch (error) {
      toast.error(error instanceof ApiRequestError ? error.message : 'Speichern fehlgeschlagen.');
    }
  }

  return (
    <section className="space-y-6">
      <PlatformPageHeader
        title="Erscheinungsbild"
        description="Titel und Hintergrundbild der Intranet-Startseite."
        action={
          <Button asChild variant="outline" size="sm">
            <Link to={INTRANET_EDITORIAL_PATH}>
              <ArrowLeftIcon aria-hidden />
              Zurück
            </Link>
          </Button>
        }
      />

      <IntranetHeroBanner
        title={shownTitle}
        backgroundUrl={preview ? null : (current?.backgroundUrl ?? null)}
        imageSrc={preview}
        backgroundAlt={shownAlt || null}
      />
      {current?.backgroundScanStatus === 'PENDING' ? (
        <p className="text-sm text-muted-foreground">
          Das Bild wird geprüft. Bis dahin bleibt die Startseite in Navy.
        </p>
      ) : null}
      {current?.backgroundScanStatus === 'BLOCKED' ? (
        <p role="alert" className="text-sm text-destructive">
          Das Bild wurde abgelehnt und wird nicht angezeigt.
        </p>
      ) : null}

      <form
        className={`${intranetPanelClassName} grid max-w-lg gap-3`}
        onSubmit={(event) => void onSubmit(event)}
      >
        <div className="space-y-1">
          <Label htmlFor="appearance-title">Titel</Label>
          <Input
            id="appearance-title"
            value={shownTitle}
            onChange={(event) => setTitle(event.target.value)}
            required
          />
        </div>
        <div className="space-y-1">
          <Label htmlFor="appearance-alt">Alternativtext des Bildes</Label>
          <Input
            id="appearance-alt"
            value={shownAlt}
            onChange={(event) => setAlt(event.target.value)}
          />
        </div>
        <div className="space-y-1">
          <Label htmlFor="appearance-file">Hintergrundbild</Label>
          <Input
            id="appearance-file"
            type="file"
            accept={INTRANET_IMAGE_ACCEPT}
            onClick={() => {
              void appearance.refetch();
            }}
            onChange={(event) => void onPickFile(event.target.files?.[0] ?? null)}
          />
          <p className="text-xs text-muted-foreground">
            JPEG, PNG oder WebP, höchstens {imageMaxMb} MB.
          </p>
        </div>
        <Button type="submit" disabled={saving || appearance.isPending}>
          Speichern
        </Button>
      </form>
    </section>
  );
}
